import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccountQuery, useTrackMutation } from '@mote/api'
import { LEGALS_VERSION, TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { Alert, Block, Spinner, WizardProvider, useWizard } from '@mote/ui'
import { SurveyComplete, SurveySubmit, WizardController } from './components'
import type { OnboardingProviderProps } from './context'
import { OnboardingProvider, useOnboarding } from './context'
import { RolesStep, getStepForRole } from './steps'
import type { Role } from './typings'
import { OnboardingRoles } from './typings'

const POLL_SLEEP = 2000

export interface OnboardingSurveyProps {
  redirectAfterFinish?: boolean
  bypassSurvey: boolean
  userOnboardingSkipped: boolean
  userOnboardingRequired: boolean
  isExtensionInstalled: boolean
  onFinish: (finishType: 'survey' | 'bypassedSurvey' | 'onboarding') => void
}

function _OnboardingSurvey(props: OnboardingSurveyProps): JSX.Element {
  const navigate = useNavigate()
  const accountQuery = useAccountQuery()
  const track = useTrackMutation()
  const wizard = useWizard()
  const { postSurvey, toggleLoading, state, isLoading, updateProperties } =
    useOnboarding()
  const [bypassedAccountQueryLoading, setBypassedAccountQueryLoading] =
    useState(props.bypassSurvey)
  const [surveyError, setSurveyError] = useState<boolean>(false)
  const [surveySuccess, setSurveySuccess] = useState<boolean>(
    props.bypassSurvey
  )
  const [currentRole, setCurrentRole] = useState<Role | null>(null)
  const accountIntervalRef = useRef<number | null>(null)

  useEffect(() => {
    if (!props.bypassSurvey) {
      return
    }

    async function fetchAccount() {
      await accountQuery.mutate()
      setBypassedAccountQueryLoading(false)
    }

    fetchAccount()
    return () => {
      if (accountIntervalRef.current) {
        clearInterval(accountIntervalRef.current)
      }
    }
  }, [])

  function handleStepsChange(role: Role | null) {
    setCurrentRole(role)
  }

  function shouldShowSubmitButton() {
    const isStudent = state.role === OnboardingRoles.student
    const isFirstStep = wizard.currentStep === 0
    return (
      !isFirstStep &&
      (!isStudent || (isStudent && Boolean(state.survey.is_minor)))
    )
  }

  function handleOnboardingQuery(): Promise<void> | undefined {
    if (props.userOnboardingRequired && !props.userOnboardingSkipped) {
      return new Promise<void>((resolve, reject) => {
        accountIntervalRef.current = window.setInterval(async () => {
          const response = await accountQuery.mutate!()

          if (response.error) {
            reject(response.error)
          }

          const { goals } = response.data ?? {}

          if (goals?.userOnboarding === 'completed') {
            if (accountIntervalRef.current) {
              clearInterval(accountIntervalRef.current)
              accountIntervalRef.current = null
            }
            resolve()
          }
        }, POLL_SLEEP)
      })
    }
  }

  async function handleAccountProperties() {
    await updateProperties({
      payload: {
        legals: LEGALS_VERSION,
        newslettersAndProductUpdatesOptIn: state.newsletterConsent,
        isMinor: Boolean(state.survey.is_minor?.value)
      }
    })
  }

  async function handleOnboardingGoal() {
    await track.mutate!({
      variables: {
        event: TRACK_EVENT_NAMES.goals.completed,
        goalName: 'userOnboarding'
      }
    })
  }

  async function handleCommonLogic() {
    /* Order matters here
     * 1. Update user's onboarding goal
     * 2. Fetch updated goals
     * 3. Update user's properties
     * 4. Post the survey
     * */
    await handleOnboardingGoal()
    await handleOnboardingQuery()
    await handleAccountProperties()
  }

  async function handleOnFinish() {
    toggleLoading()
    try {
      await handleCommonLogic()
      await postSurvey()
      props.onFinish('survey')
      setSurveySuccess(true)
    } catch {
      setSurveyError(true)
    } finally {
      toggleLoading()
    }
  }

  async function handleContinue() {
    if (props.bypassSurvey) {
      toggleLoading()
      try {
        await handleCommonLogic()
        props.onFinish('bypassedSurvey')
      } catch {
        setSurveyError(true)
      } finally {
        toggleLoading()
      }
    } else {
      props.onFinish('onboarding')
    }
    //TODO: shouldn't that be decided by parent?
    if (props.redirectAfterFinish) {
      navigate('/')
    }
  }

  if (props.bypassSurvey && bypassedAccountQueryLoading) {
    return (
      <Block align='center' justify='center' spreadX spreadY>
        <Spinner radius={65} />
      </Block>
    )
  }

  if (surveySuccess) {
    return (
      <Block spreadX maxWidth={400}>
        <SurveyComplete
          isExtensionInstalled={props.isExtensionInstalled}
          onContinue={handleContinue}
          account={(accountQuery.data as any)?.account}
        />
      </Block>
    )
  }

  const StepForRole = getStepForRole(currentRole)

  return (
    <Block spreadX maxWidth={400}>
      <Block gap={24} spreadX>
        <Block gap={20} spreadX>
          <WizardController />
          <RolesStep index={0} updateSteps={handleStepsChange} />
          <StepForRole index={1} />
        </Block>
        {shouldShowSubmitButton() && (
          <SurveySubmit onSubmit={handleOnFinish} isLoading={isLoading} />
        )}
        {surveyError && (
          <Alert
            type='error'
            fontSize={16}
            message='Something went wrong while completing your onboarding. Please try again later or if the error persists, kindly contact our support team'
          />
        )}
      </Block>
    </Block>
  )
}

export function OnboardingSurvey({
  institutionNameHints,
  surveyContext,
  canSchoolSearchDuringUserOnboarding,
  ...onboardingSurveyProps
}: OnboardingSurveyProps & OnboardingProviderProps) {
  return (
    <OnboardingProvider
      surveyContext={surveyContext}
      institutionNameHints={institutionNameHints}
      canSchoolSearchDuringUserOnboarding={canSchoolSearchDuringUserOnboarding}
    >
      <WizardProvider totalSteps={2}>
        <_OnboardingSurvey {...onboardingSurveyProps} />
      </WizardProvider>
    </OnboardingProvider>
  )
}
